/* eslint-disable @typescript-eslint/no-explicit-any */
import { cloneDeep } from 'lodash'
import { APIServer } from '@/app/data/api'
import type {
  DocEditPageObj,
  DocItem,
  ExportTemplate,
  FflateLib,
  ScriptModule,
  ShapeForPhotos,
  TreeViewData,
  TreeViewDoc,
  UserExportReport,
  UserExportReportRecord,
} from '@/app/data/type'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { SQL } from '@/lib/Sqlite'
import { checkAppNetwork, getItemToProcess, parseRow } from '@/app/util'
import { Network } from '@capacitor/network'
import { getRandomString, getUnixtime } from '../../../lib'
import { GCS_ROOT_PATH, isApp } from '../../..'
import { prepareSQLiteValue } from '../../../lib/Sqlite/schema'
import { JobPriority, JobStatus, syncQueue } from '../../sync'
import { filterStaleMergedItems } from '../../sync/mergeWriteGuard'
import { CloudFile } from '../../../lib/CloudFile'
import { useAppStateStore } from '../../store/state'
import { AppFileManager } from '../../file'
import { LocalFileResourcePath } from '../../camera/type'
import { convertFileToWebp } from '../../camera'
import { App } from '../..'
import { getDocEditDocumentTypeList, getDocEditDocumentTypeListValue } from '../../constant/docs'
import { Facility, Task } from '../types'
import SQLite from '../../../lib/Task/sqlite'
import Merge from '../../../lib/Task/merge'
import { DocCanvasBrushEntity } from '../../../lib/DocCanvas/brushController'
import { base64ToFile } from '@/lib/Functions/files'
import { useAllTaskStore } from '../../store/task'

const storeKey = 'docs'
const TYPE_LIST = ['DOC', 'SUPPLEMENT']
const SCRIPT_FILE_NAME = 'data.js' // 추후 사용 예정

declare global {
  interface Window {
    fflate?: FflateLib
  }
}

const CREATE_TABLE_DATA: UserExportReportRecord = {
  id: 0,
  task_id: 0,
  company_id: 0,
  facility_id: 0,
  template_id: 0,
  page_no: 0,
  items: '',
  created_at: '',
  updated_at: '',
  deleted_at: null,
  brush_data: [],
  fields_updated_at: {},
  localStringId: '',
  modifiedFromLocal: false,
  url: '',
}

// fflate 동적 로드
async function loadFflate(): Promise<FflateLib> {
  return new Promise((resolve, reject) => {
    if (window.fflate) {
      resolve(window.fflate)
      return
    }
    const script = document.createElement('script')
    script.src = '/assets/js/fflate.min.js'
    script.onload = (): void => {
      if (window.fflate) resolve(window.fflate)
      else reject(new Error('fflate library not loaded'))
    }
    script.onerror = (): void => reject(new Error('Failed to load fflate library'))
    document.head.appendChild(script)
  })
}

const getLocalData = async (taskId: string): Promise<UserExportReportRecord[]> => {
  if (!taskId) return []
  const db = await SQL.getDatabase()
  try {
    // 테이블이 없는 경우 빈 배열 반환
    if (!SQL.createdTables.has(storeKey)) {
      throw new Error('테이블이 존재하지 않습니다.')
    }

    // 테이블이 존재하는 경우에만 쿼리 실행
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
    return localData.values?.map((row: UserExportReportRecord) =>
      parseRow(row),
    ) as UserExportReportRecord[]
  } catch (error) {
    console.error('로컬 결함 가져오기 실패', error)
    return []
  }
}

export const useDocStore = defineStore(storeKey, () => {
  // API/Store 데이터: 플랫한 구조 (원본 데이터)
  const docRecords = ref<UserExportReportRecord[]>([])
  const currentTaskDatabaseId = ref(0)
  // 과업 진입은 문서 준비를 기다리지 않는다 — 준비 중이면 문서 작성 화면이 '준비 중'을 보이고 끝나면 첫 페이지를 연다.
  // 세대는 늦게 끝난 이전 과업의 준비가 새 과업의 문서 상태·로컬 DB를 덮어쓰지 않게 하는 표시
  const isPreparing = ref(false)
  let initGeneration = 0
  let initPromise: Promise<void> = Promise.resolve()
  const currentCompanyCode = ref<number | null>(null)
  // UI 선택 상태
  const selectedFacilityId = ref<number | null>(null)
  const selectedTemplateKey = ref<number | null>(null)
  const selectedTemplateId = ref<number | null>(null)

  //노드
  const selectedNode = ref<TreeViewDoc | null>(null)

  // 페이지별 업데이트 락 - 같은 페이지의 동시 업데이트 방지
  const pageUpdateLocks = new Map<string, Promise<void>>()

  /**
   * 페이지별 락을 획득하고 작업 실행
   * 같은 페이지에 대한 업데이트는 순차적으로 처리됨
   * @param pageKey - 페이지 식별 키
   * @param operation - 실행할 작업
   * @param timeout - 타임아웃 (밀리초, 기본값: 30000ms = 30초)
   */
  async function withPageLock<T>(
    pageKey: string,
    operation: () => Promise<T>,
    timeout = 30000,
  ): Promise<T> {
    // 현재 페이지에 진행 중인 작업이 있으면 대기
    const existingLock = pageUpdateLocks.get(pageKey)
    if (existingLock) {
      await existingLock
    }

    // 새 작업의 Promise 생성
    let resolveLock: () => void
    const lockPromise = new Promise<void>(resolve => {
      resolveLock = resolve
    })
    pageUpdateLocks.set(pageKey, lockPromise)

    try {
      // 타임아웃 처리
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Page update timeout (${timeout}ms) for key: ${pageKey}`))
        }, timeout)
      })

      return await Promise.race([operation(), timeoutPromise])
    } finally {
      // 작업 완료 후 락 해제
      resolveLock!()
      // 현재 락이 여전히 이 작업의 것인지 확인 후 삭제
      if (pageUpdateLocks.get(pageKey) === lockPromise) {
        pageUpdateLocks.delete(pageKey)
      }
    }
  }

  function allClear(): void {
    docRecords.value = []
    currentReportData.value = []
    selectedFacilityId.value = null
    selectedTemplateId.value = null
    selectedNode.value = null
    currentFacilityPhotoItems.value = []
    settedShapeTableData.value = []
    cachedTemplateModules.value = []
  }

  async function individualSetLocalModified(
    localStringId: string, // data: LocalLayer 대신 localStringId를 받도록 변경
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    const db = await SQL.getDatabase()
    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      const keyValue = keyValues[i]
      sql += `${keyValue.key} = ?`
      if (i < keyValues.length - 1) {
        sql += ', '
      }
    }
    sql += ' WHERE localStringId = ?' // WHERE 조건도 localStringId로 변경
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    const docRecordIndex = docRecords.value.findIndex((item: UserExportReportRecord) => {
      // id 또는 localStringId로 비교 (id가 없을 수 있음)
      const itemId = item.id ? item.id.toString() : item.localStringId
      return itemId === localStringId
    })
    if (docRecordIndex !== -1) {
      const docRecordToUpdate = docRecords.value[docRecordIndex]
      for (const keyValue of keyValues) {
        ;(docRecordToUpdate as unknown as Record<string, unknown>)[keyValue.key] = keyValue.value
      }
    }
  }

  /**
   * 특정 페이지의 아이템 값을 업데이트
   * 페이지별 락을 사용하여 같은 페이지의 동시 업데이트로 인한 race condition 방지
   * @param {number} taskId - 과업 ID
   * @param {number} facilityId - 시설물 ID
   * @param {number} templateId - 템플릿 ID
   * @param {number} pageNo - 페이지 번호
   * @param {DocEditPageObj} item - 아이템 데이터
   * @param {string} value - 새로운 값
   */
  async function updatePageItemValue(
    taskId: number,
    facilityId: number,
    templateId: number,
    pageNo: number,
    item: DocEditPageObj,
    value: string,
  ): Promise<void> {
    // 진입 직후 문서 준비 중이면 레코드가 아직 없거나 준비 끝 교체로 덮이므로 준비가 끝난 뒤 쓴다
    await whenRecordsReady()
    // 페이지별 락을 사용하여 같은 페이지의 업데이트는 순차적으로 처리
    const pageKey = `${taskId}_${facilityId}_${templateId}_${pageNo}`

    return withPageLock(pageKey, async () => {
      try {
        // 락 획득 후 최신 레코드를 다시 조회 (이전 업데이트가 반영된 상태)
        const recordIndex = docRecords.value.findIndex(
          r =>
            r.task_id == taskId &&
            r.template_id == templateId &&
            r.facility_id == facilityId &&
            r.page_no == pageNo,
        )

        if (recordIndex === -1) {
          console.error('레코드를 찾을 수 없습니다:', { facilityId, templateId, taskId })
          return
        }

        const record = docRecords.value[recordIndex]

        if (record && record.items) {
          try {
            // 최신 items 배열을 파싱 (이전 업데이트가 반영된 상태)
            const items: DocEditPageObj[] =
              typeof record.items === 'string' ? JSON.parse(record.items) : [...record.items]

            const itemIndex = items.findIndex(i => i.id == item.id)
            if (itemIndex === -1) {
              console.error('아이템을 찾을 수 없습니다:', { id: item.id, facilityId, templateId })
              return
            }

            // 값 업데이트 - multi_file의 경우 items도 업데이트
            if (item.items && Array.isArray(item.items)) {
              // multi_file 타입: items 배열 업데이트
              items[itemIndex] = {
                ...items[itemIndex],
                items: item.items,
                value,
              }
            } else {
              // 일반 타입: value만 업데이트
              items[itemIndex] = {
                ...items[itemIndex],
                value,
              }
            }

            // 레코드 업데이트 (새 객체로 교체하여 reactivity 보장)
            const updatedRecord = {
              ...record,
              items: items,
              updated_at: Merge.getCurrentTime(),
            }
            docRecords.value[recordIndex] = updatedRecord

            //record에 id가 없다는건 서버에 업로드되지 않은 input만 존재한다는것
            const isNotUploadedReocords = !docRecords.value[recordIndex].id
            if (isApp()) {
              if (!updatedRecord.id && !updatedRecord.localStringId) {
                const newLocalStringId = getRandomString(10, false)
                updatedRecord.id = `${newLocalStringId}_need_to_upload`
                updatedRecord.localStringId = newLocalStringId
              }
              updatedRecord.fields_updated_at = {
                ...updatedRecord.fields_updated_at,
                items: Merge.getCurrentTime(),
              }
              updatedRecord.updated_at = Merge.getCurrentTime()
              updatedRecord.modifiedFromLocal = true
              await requestUpdate([updatedRecord])
              await updateOhterValue(taskId, facilityId, item.id, value)
              await reloadCurrentReportDataOnRecords()
            } else {
              //id가 없다는건 서버에 업로드되지 않은 input만 존재한다는것
              if (isNotUploadedReocords) {
                const [newRecord] = await APIServer.addUserExportReportRecord([
                  {
                    facility_id: updatedRecord.facility_id as number,
                    template_id: updatedRecord.template_id,
                    task_id: updatedRecord.task_id as number,
                    page_no: updatedRecord.page_no,
                    items: updatedRecord.items,
                  },
                ])
                docRecords.value[recordIndex].id = newRecord.id
                await updateOhterValue(taskId, facilityId, item.id, value)
                await reloadCurrentReportDataOnRecords()
              } else {
                const needUpdate = {
                  id: updatedRecord.id,
                  items: updatedRecord.items,
                }
                await APIServer.updateUserExportReportRecord([needUpdate])

                await updateOhterValue(taskId, facilityId, item.id, value)
                await reloadCurrentReportDataOnRecords()
              }
            }
          } catch (error) {
            console.error('아이템 값 업데이트 오류:', error)
          }
        }
      } catch (error) {
        console.error('아이템 값 업데이트 오류:', error)
      }
    })
  }

  /**
   * 페이지의 전체 브러시 데이터 동기화 (Undo/Redo용)
   * @param taskId - 작업 ID
   * @param facilityId - 시설 ID
   * @param templateId - 템플릿 ID
   * @param pageNo - 페이지 번호
   * @param brushEntities - 모든 브러시 엔티티 배열
   */
  async function syncPageBrushData(
    taskId: number,
    facilityId: number,
    templateId: number,
    pageNo: number,
    brushEntities: DocCanvasBrushEntity[],
  ): Promise<void> {
    const stateStore = useAppStateStore()
    try {
      stateStore.setIsLoading(true)
      const recordIndex = docRecords.value.findIndex(
        r =>
          r.task_id == taskId &&
          r.template_id == templateId &&
          r.facility_id == facilityId &&
          r.page_no == pageNo,
      )

      if (recordIndex === -1) {
        console.error('레코드를 찾을 수 없습니다:', { facilityId, templateId, taskId })
        return
      }

      const record = docRecords.value[recordIndex]
      if (record) {
        try {
          // 모든 엔티티의 데이터를 직렬화
          const brushData = brushEntities.map(entity => entity.toFullData())

          // 레코드 업데이트
          const updatedRecord = {
            ...record,
            updated_at: Merge.getCurrentTime(),
            brush_data: brushData,
          }
          docRecords.value[recordIndex] = updatedRecord

          const isNotUploadedReocords = !docRecords.value[recordIndex].id
          if (isApp()) {
            if (!updatedRecord.id && !updatedRecord.localStringId) {
              const newLocalStringId = getRandomString(10, false)
              updatedRecord.id = `${newLocalStringId}_need_to_upload`
              updatedRecord.localStringId = newLocalStringId
            }
            updatedRecord.fields_updated_at = {
              ...updatedRecord.fields_updated_at,
              brush_data: Merge.getCurrentTime(),
            }
            updatedRecord.updated_at = Merge.getCurrentTime()
            updatedRecord.modifiedFromLocal = true
            await requestUpdate([updatedRecord])
            await reloadCurrentReportDataOnRecords()
          } else {
            if (isNotUploadedReocords) {
              const [newRecord] = await APIServer.addUserExportReportRecord([
                {
                  facility_id: updatedRecord.facility_id as number,
                  template_id: updatedRecord.template_id,
                  task_id: updatedRecord.task_id as number,
                  page_no: updatedRecord.page_no,
                  items: updatedRecord.items,
                  brush_data: updatedRecord.brush_data,
                },
              ])
              docRecords.value[recordIndex].id = newRecord.id
              await reloadCurrentReportDataOnRecords()
            } else {
              const needUpdate = {
                id: updatedRecord.id,
                items: updatedRecord.items,
                brush_data: updatedRecord.brush_data,
              }
              await APIServer.updateUserExportReportRecord([needUpdate])
              await reloadCurrentReportDataOnRecords()
            }
          }
        } catch (error) {
          console.error('브러시 데이터 동기화 오류:', error)
        }
      }
    } catch (error) {
      console.error('브러시 데이터 동기화 오류:', error)
    } finally {
      stateStore.setIsLoading(false)
    }
  }

  async function updatePageBrushValue(
    taskId: number,
    facilityId: number,
    templateId: number,
    pageNo: number,
    brushEntity: DocCanvasBrushEntity,
  ): Promise<void> {
    const stateStore = useAppStateStore()
    try {
      stateStore.setIsLoading(true)
      const recordIndex = docRecords.value.findIndex(
        r =>
          r.task_id == taskId &&
          r.template_id == templateId &&
          r.facility_id == facilityId &&
          r.page_no == pageNo,
      )

      if (recordIndex === -1) {
        console.error('레코드를 찾을 수 없습니다:', { facilityId, templateId, taskId })
        return
      }

      const record = docRecords.value[recordIndex]
      if (record) {
        try {
          let brushData = record.brush_data ?? []
          // brush_data가 string이라면 JSON parse 시도
          if (typeof brushData === 'string') {
            try {
              brushData = JSON.parse(brushData)
            } catch (e) {
              console.error('Failed to parse brush_data:', e)
              brushData = []
            }
          }
          const cloneBrushData = cloneDeep(brushData)
          const findEntityIndex = cloneBrushData.findIndex(
            (entity: any) => entity.id === brushEntity.getId(),
          )
          if (findEntityIndex !== -1) {
            cloneBrushData[findEntityIndex] = brushEntity.toFullData()
          } else {
            cloneBrushData.push(brushEntity.toFullData())
          }
          // 레코드 업데이트 (새 객체로 교체하여 reactivity 보장)
          const updatedRecord = {
            ...record,
            updated_at: Merge.getCurrentTime(),
            brush_data: cloneBrushData,
          }
          docRecords.value[recordIndex] = updatedRecord
          //record에 id가 없다는건 서버에 업로드되지 않은 input만 존재한다는것
          const isNotUploadedReocords = !docRecords.value[recordIndex].id
          if (isApp()) {
            if (!updatedRecord.id && !updatedRecord.localStringId) {
              const newLocalStringId = getRandomString(10, false)
              updatedRecord.id = `${newLocalStringId}_need_to_upload`
              updatedRecord.localStringId = newLocalStringId
            }
            updatedRecord.fields_updated_at = {
              ...updatedRecord.fields_updated_at,
              brush_data: Merge.getCurrentTime(),
            }
            updatedRecord.updated_at = Merge.getCurrentTime()
            updatedRecord.modifiedFromLocal = true
            await requestUpdate([updatedRecord])
            await reloadCurrentReportDataOnRecords()
          } else {
            //id가 없다는건 서버에 업로드되지 않은 input만 존재한다는것
            if (isNotUploadedReocords) {
              const [newRecord] = await APIServer.addUserExportReportRecord([
                {
                  facility_id: updatedRecord.facility_id as number,
                  template_id: updatedRecord.template_id,
                  task_id: updatedRecord.task_id as number,
                  page_no: updatedRecord.page_no,
                  items: updatedRecord.items,
                  brush_data: updatedRecord.brush_data,
                },
              ])
              docRecords.value[recordIndex].id = newRecord.id
              await reloadCurrentReportDataOnRecords()
            } else {
              const needUpdate = {
                id: updatedRecord.id,
                items: updatedRecord.items,
                brush_data: updatedRecord.brush_data,
              }
              // docRecords.value[recordIndex] = {
              //   ...updatedRecord,
              //   items: updatedRecord.items,
              // }
              await APIServer.updateUserExportReportRecord([needUpdate])
              await reloadCurrentReportDataOnRecords()
            }
          }
        } catch (error) {
          console.error('브러시 데이터 업데이트 오류:', error)
        }
      }
    } catch (error) {
      console.error('브러시 데이터 업데이트 오류:', error)
    } finally {
      stateStore.setIsLoading(false)
    }
  }

  /**
   * 페이지의 브러시 엔티티 삭제
   * @param taskId - 작업 ID
   * @param facilityId - 시설 ID
   * @param templateId - 템플릿 ID
   * @param pageNo - 페이지 번호
   * @param brushEntityId - 삭제할 브러시 엔티티 ID
   */
  async function deletePageBrushValue(
    taskId: number,
    facilityId: number,
    templateId: number,
    pageNo: number,
    brushEntityId: string,
  ): Promise<void> {
    const stateStore = useAppStateStore()
    try {
      stateStore.setIsLoading(true)

      // 현재 페이지 레코드 찾기
      const recordIndex = docRecords.value.findIndex(
        r =>
          r.task_id == taskId &&
          r.template_id == templateId &&
          r.facility_id == facilityId &&
          r.page_no == pageNo,
      )

      if (recordIndex === -1) {
        console.error('레코드를 찾을 수 없습니다:', { facilityId, templateId, taskId, pageNo })
        return
      }

      const record = docRecords.value[recordIndex]
      if (record) {
        try {
          let brushData = record.brush_data ?? []
          // brush_data가 string이라면 JSON parse 시도
          if (typeof brushData === 'string') {
            try {
              brushData = JSON.parse(brushData)
            } catch (e) {
              console.error('Failed to parse brush_data:', e)
              brushData = []
            }
          }
          const cloneBrushData = cloneDeep(brushData)

          // 해당 엔티티 제거
          const updatedBrushData = cloneBrushData.filter(
            (entity: any) => entity.id !== brushEntityId,
          )

          // 레코드 업데이트 (새 객체로 교체하여 reactivity 보장)
          const updatedRecord = {
            ...record,
            updated_at: Merge.getCurrentTime(),
            brush_data: updatedBrushData,
          }
          docRecords.value[recordIndex] = updatedRecord

          if (isApp()) {
            if (!updatedRecord.id && !updatedRecord.localStringId) {
              const newLocalStringId = getRandomString(10, false)
              updatedRecord.id = `${newLocalStringId}_need_to_upload`
              updatedRecord.localStringId = newLocalStringId
            }
            updatedRecord.updated_at = Merge.getCurrentTime()
            updatedRecord.modifiedFromLocal = true
            await requestUpdate([updatedRecord])
            await reloadCurrentReportDataOnRecords()
          } else {
            const needUpdate = {
              id: updatedRecord.id,
              items: updatedRecord.items,
              brush_data: updatedRecord.brush_data,
            }
            // docRecords.value[recordIndex] = {
            //   ...updatedRecord,
            //   items: updatedRecord.items,
            // }
            await APIServer.updateUserExportReportRecord([needUpdate])
            await reloadCurrentReportDataOnRecords()
          }
        } catch (error) {
          console.error('[DocStore] 브러시 삭제 중 오류:', error)
          throw error
        }
      }
    } catch (error) {
      console.error('[DocStore] 브러시 삭제 실패:', error)
      throw error
    } finally {
      stateStore.setIsLoading(false)
    }
  }

  //taskId, facilityId, itemId를 기반으로 레코드를 찾아 리턴
  //모든 레코드의 items에서 id가 itemId인 아이템을 찾아 리턴
  function findRecordByTaskIdAndFacilityIdAndItemId(
    taskId: number,
    facilityId: number,
    itemId: string,
  ): UserExportReportRecord | null {
    const record = docRecords.value.find(r => {
      if (r.task_id == taskId && r.facility_id == facilityId) {
        if (r.items && Array.isArray(r.items)) {
          return r.items.some((i: DocEditPageObj) => i.id == itemId)
        }
        return false
      }
      return false
    })
    return record || null
  }

  async function findInputKeyAndUpdate(
    taskId: number,
    facilityId: number,
    inputKey: string,
    value: string,
    unitValue?: string,
    updateItems?: any[],
  ): Promise<void> {
    if (!inputKey || inputKey === '') {
      return
    }
    const record = findRecordByTaskIdAndFacilityIdAndItemId(taskId, facilityId, inputKey)
    const recordIndex = docRecords.value.findIndex(
      r => r.id == record?.id || r.localStringId == record?.localStringId,
    )
    if (!record || recordIndex === -1) {
      console.error('연결된 레코드를 찾을 수 없습니다:', { facilityId, inputKey, taskId })
      return
    }

    try {
      const items: any[] = record.items as any[]

      const findItemIndex = items.findIndex((i: any) => i.id == inputKey)
      if (findItemIndex === -1) {
        console.error('연결된 아이템을 찾을 수 없습니다:', {
          id: inputKey,
          facilityId,
          taskId,
        })
        return
      }
      items[findItemIndex] = {
        ...items[findItemIndex],
        value: `${value}${unitValue ? ` ${unitValue}` : ''}`,
      }
      if (updateItems) {
        items[findItemIndex].items = updateItems
      }

      const updatedRecord = {
        ...record,
        items: items,
        updated_at: Merge.getCurrentTime(),
      }
      docRecords.value[recordIndex] = updatedRecord

      const isNotUploadedReocords = !docRecords.value[recordIndex].id
      if (isApp()) {
        if (!updatedRecord.id && !updatedRecord.localStringId) {
          const newLocalStringId = getRandomString(10, false)
          updatedRecord.id = `${newLocalStringId}_need_to_upload`
          updatedRecord.localStringId = newLocalStringId
        }
        updatedRecord.updated_at = Merge.getCurrentTime()
        updatedRecord.modifiedFromLocal = true
        await requestUpdate([updatedRecord])
      } else {
        if (isNotUploadedReocords) {
          const [newRecord] = await APIServer.addUserExportReportRecord([
            {
              facility_id: updatedRecord.facility_id as number,
              template_id: updatedRecord.template_id,
              task_id: updatedRecord.task_id as number,
              page_no: updatedRecord.page_no,
              items: updatedRecord.items,
            },
          ])
          docRecords.value[recordIndex].id = newRecord.id
        } else {
          const needUpdate = {
            id: updatedRecord.id,
            items: updatedRecord.items,
          }
          await APIServer.updateUserExportReportRecord([needUpdate])
        }
      }
    } catch (error) {
      console.error('아이템 값 업데이트 오류:', error)
    }
  }

  //특정 키를 해당 value로 똑같이 업데이트하는 함수
  //모든것을 대응할 수 있을지..?
  async function updateOhterValue(
    taskId: number,
    facilityId: number,
    currentInputKey: string,
    value: string,
  ): Promise<void> {
    if (!currentInputKey || currentInputKey === '') {
      return
    }
    switch (currentInputKey) {
      case 'A-1-7':
        await findInputKeyAndUpdate(
          taskId,
          facilityId,
          'A-1-8',
          value,
          '',
          getDocEditDocumentTypeList(value),
        )
        break
      case 'A-1-9':
        await findInputKeyAndUpdate(
          taskId,
          facilityId,
          'A-1-12',
          value,
          '',
          getDocEditDocumentTypeListValue(value),
        )
        break
      case 'A-1-12':
        await findInputKeyAndUpdate(taskId, facilityId, 'G-2-2', value, '등급')
        await findInputKeyAndUpdate(taskId, facilityId, 'G-2-5', value, '등급')
        break
      case 'J-1-1-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-2-4', value)
        break
      case 'J-1-2-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-2-5', value)
        break
      case 'J-1-3-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-2-6', value)
        break
      case 'J-1-4-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-3-4', value)
        break
      case 'J-1-5-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-3-5', value)
        break
      case 'J-1-6-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-3-6', value)
        break
      case 'J-1-7-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-4-4', value)
        break
      case 'J-1-8-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-4-5', value)
        break
      case 'J-1-9-1':
        await findInputKeyAndUpdate(taskId, facilityId, 'J-4-6', value)
        break
      default:
        break
    }
  }

  /**
   * 현재 docRecords를 기반으로 currentReportData를 다시 생성
   * 기존 구조를 유지하면서 pages만 최신 docRecords로 교체
   * @returns {Promise<void>}
   */
  async function reloadCurrentReportDataOnRecords(): Promise<void> {
    // 기존 currentReportData 클론
    const clonedReportData = cloneDeep(currentReportData.value)

    // facility별로 순회
    for (const facility of clonedReportData) {
      if (!facility.children) continue

      // category(DOC 등)별로 순회
      for (const category of facility.children) {
        if (!category.children) continue

        // 실제 문서(DocItem)별로 순회
        for (const doc of category.children) {
          const docItem = doc as unknown as DocItem
          if (!docItem.pages) continue

          // 해당 template의 모든 pages를 docRecords에서 찾아서 교체
          const updatedPages: UserExportReportRecord[] = []

          for (const page of docItem.pages) {
            // docRecords에서 매칭되는 record 찾기
            const matchingRecord = docRecords.value.find(
              (record: UserExportReportRecord) =>
                record.task_id == docItem.task_id &&
                record.facility_id == docItem.facility_id &&
                record.template_id == docItem.template_id &&
                record.page_no == page.page_no,
            )

            if (matchingRecord) {
              // items가 string인 경우 파싱
              const parsedRecord = {
                ...matchingRecord,
                items:
                  typeof matchingRecord.items === 'string'
                    ? (JSON.parse(matchingRecord.items) as DocEditPageObj[])
                    : matchingRecord.items,
              }
              updatedPages.push(parsedRecord as UserExportReportRecord)
            } else {
              // 매칭되는 record가 없으면 기존 page 유지
              updatedPages.push(page)
            }
          }

          // 교체된 pages로 업데이트
          docItem.pages = updatedPages
        }
      }
    }

    // 업데이트된 데이터로 교체
    currentReportData.value = clonedReportData

    // selectedNode 업데이트 - 선택된 문서가 있으면 최신 데이터로 갱신
    if (selectedFacilityId.value && selectedTemplateId.value && selectedNode.value) {
      const findFacility = currentReportData.value.find(
        (node: TreeViewData) => node.id == selectedFacilityId.value?.toString(),
      )
      if (findFacility && findFacility.children) {
        // DOC 카테고리 찾기
        for (const category of findFacility.children) {
          if (!category.children) continue

          // 실제 문서 찾기
          const findTemplate = category.children.find(
            (child: { template_id?: number }) =>
              child.template_id?.toString() == selectedTemplateId.value?.toString(),
          )
          if (findTemplate) {
            selectedNode.value = cloneDeep(findTemplate) as TreeViewDoc
            break
          }
        }
      }
    }
  }
  function mergeRecordToDefaultGenData(
    records: UserExportReportRecord[],
    defaultGenData: TreeViewDoc,
  ): TreeViewDoc {
    // defaultGenData.pages를 복사하여 시작
    const mergedPages = [...defaultGenData.pages]
    // 각 record를 순회하면서 조건에 맞는 페이지를 교체
    for (const record of records) {
      const facilityId = record.facility_id
      const pageNo = record.page_no
      const templateId = record.template_id
      const taskId = record.task_id

      // 조건에 맞는 페이지의 인덱스 찾기
      const pageIndex = mergedPages.findIndex(
        page =>
          page.page_no == pageNo &&
          page.facility_id == facilityId &&
          page.template_id == templateId &&
          page.task_id == taskId,
      )

      // 조건에 맞는 페이지가 있으면 병합
      if (pageIndex !== -1) {
        const defaultPage = mergedPages[pageIndex]
        const recordItems =
          typeof record.items == 'string' ? JSON.parse(record.items) : record.items

        // items 병합: record에 있는 item은 record의 값 사용, 없으면 default 값 사용
        const mergedItems = defaultPage.items.map((defaultItem: DocEditPageObj) => {
          const recordItem = recordItems.find((rItem: DocEditPageObj) => rItem.id == defaultItem.id)
          if (recordItem) {
            // record에 해당 item이 있으면 record의 값 사용 (위치/크기는 default 유지)
            return {
              ...defaultItem,
              value: recordItem.value,
              items: recordItem.items,
            }
          }
          // record에 없으면 default 값 사용
          return defaultItem
        })

        // record에만 있고 default에 없는 새로운 item이 있다면 추가 (템플릿 업데이트 시)
        const newItems = recordItems.filter(
          (recordItem: DocEditPageObj) =>
            !defaultPage.items.some(
              (defaultItem: DocEditPageObj) => defaultItem.id == recordItem.id,
            ),
        )

        mergedPages[pageIndex] = {
          ...record,
          url: defaultPage.url,
          items: [...mergedItems, ...newItems],
        }
      }
    }

    return {
      ...defaultGenData,
      pages: mergedPages,
    }
  }

  async function getAllModifiedData(): Promise<UserExportReportRecord[]> {
    const db = await SQL.getDatabase()
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return localData.values?.map((row: unknown) => parseRow(row)) as UserExportReportRecord[]
  }

  async function getServerData(taskData: Task): Promise<UserExportReportRecord[]> {
    if (!taskData.id || taskData.facilities.length === 0) return []
    try {
      const serverPageData = await APIServer.getUserExportReportRecordList(taskData.id)
      if (!serverPageData || serverPageData.length === 0) return []

      return serverPageData.map(item => {
        return {
          ...item,
          localStringId: item.id.toString(),
          modifiedFromLocal: false,
          deleted_at: null,
        }
      })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }
  async function getCurrentServerData(
    task_id: number,
    facility_id: number,
    template_id: number,
  ): Promise<UserExportReportRecord[]> {
    if (!task_id || !facility_id || !template_id) return []
    try {
      const serverPageData = await APIServer.getUserExportReportRecordListCurrent(
        task_id,
        facility_id,
        template_id,
      )
      if (!serverPageData || serverPageData.length === 0) return []

      return serverPageData.map(item => {
        return {
          ...item,
          // 타입 정규화
          task_id: Number(item.task_id),
          facility_id: Number(item.facility_id),
          template_id: Number(item.template_id),
          page_no: Number(item.page_no),
          // items가 배열이면 문자열로 변환 (다른 로직과 일관성 유지)
          items: typeof item.items === 'string' ? item.items : JSON.stringify(item.items),
          brush_data: item.brush_data ?? [],
          localStringId: item.id.toString(),
          modifiedFromLocal: false,
          deleted_at: null,
        }
      })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  async function syncLocalAndServerRecords(taskData: Task, gen: number): Promise<void> {
    let mergedData: UserExportReportRecord[] = []
    try {
      let didMerge = false
      if (isApp()) {
        const localData = await getLocalData(taskData.id.toString())

        mergedData = localData

        await SQLite.checkTableExists(storeKey, [CREATE_TABLE_DATA])
        if ((await Network.getStatus()).connected) {
          const serverData = await getServerData(taskData)

          // 서버가 빈 배열인데 로컬에 비삭제 데이터가 있으면 merge 스킵 (데이터 소실 방지)
          const activeLocalData = localData.filter(item => !item.deleted_at)
          if (serverData.length > 0 || activeLocalData.length === 0) {
            mergedData = Merge.mergeBothData(
              localData,
              serverData,
              storeKey,
            ) as UserExportReportRecord[]
            didMerge = true
          }
        }
      } else {
        mergedData = await getServerData(taskData)
      }

      // 그사이 다른 과업(또는 같은 과업 재진입)의 준비가 시작됐으면 쓰지 않는다
      if (gen !== initGeneration) return
      if (mergedData.length > 0) {
        docRecords.value = mergedData
        if (!isApp()) {
          return
        }
        if (didMerge) {
          // 병합 결과 저장: 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 제외
          // (유령 row 부활 → 재업로드로 인한 서버 중복 방지)
          const { itemsToProcess, columns } = await getItemToProcess(mergedData, storeKey)
          const safeItems = await filterStaleMergedItems(
            itemsToProcess as { item: UserExportReportRecord; itemId: string }[],
            storeKey,
            taskData.id.toString(),
          )
          await updateSqliteOnly(safeItems, columns)
          await addApiQueue(safeItems)
        }
      }
    } catch (e) {
      console.error('Error in initDataLocal:', e)
      throw e
    }
  }

  function initData(taskData: Task): Promise<void> {
    if (!taskData) return Promise.resolve()
    const gen = ++initGeneration
    currentTaskDatabaseId.value = taskData.id
    currentCompanyCode.value = taskData.company_id
    isPreparing.value = true
    const run = (async (): Promise<void> => {
      try {
        await syncLocalAndServerRecords(taskData, gen)
        // 추월된 회차는 템플릿 받기·문서 생성을 하지 않는다
        if (gen !== initGeneration) return
        await initAllData(taskData, gen)
      } finally {
        if (gen === initGeneration) isPreparing.value = false
      }
    })()
    initPromise = run.catch(() => {})
    return run
  }

  /** 문서 레코드 교체가 끝날 때까지 — whenReady와 달리 보고서 이미지 미리 받기는 기다리지 않는다 */
  function whenRecordsReady(): Promise<void> {
    if (!isPreparing.value) return Promise.resolve()
    return new Promise(resolve => {
      const stop = watch(isPreparing, preparing => {
        if (preparing) return
        stop()
        resolve()
      })
    })
  }

  /** 진행 중인 문서 준비가 끝날 때까지(실패도 끝남으로) */
  function whenReady(): Promise<void> {
    return initPromise
  }

  /**
   * 템플릿 zip 파일을 다운로드하고 scriptModule을 추출
   * @param template - 템플릿 정보
   * @param networkStatus - 네트워크 상태
   * @param fflate - fflate 라이브러리
   * @returns template과 scriptModule
   */
  async function loadTemplateScriptModule(
    template: ExportTemplate,
    networkStatus: { connected: boolean },
    fflate: FflateLib,
  ): Promise<{ template: ExportTemplate; scriptModule: ScriptModule }> {
    // 오프라인이면 로컬 사본 확인(온라인은 아래에서 한 번 받아 쓰고 그 내용을 사본으로 저장)
    const fileStatus = networkStatus.connected
      ? { downloaded: false, localPath: null }
      : await AppFileManager.checkAndUpdateFile(template.url, false)

    let templateBuffer: ArrayBuffer

    // 인터넷이 연결되어 있거나 로컬 파일이 있는 경우
    if (networkStatus.connected || fileStatus.localPath) {
      if (networkStatus.connected) {
        // 인터넷 연결: 원격에서 최신 파일 다운로드
        const templateRes = await fetch(CloudFile.ResourceUrlToSignedUrl(template.url))
        if (!templateRes.ok) throw new Error(`Failed to download template: ${templateRes.status}`)
        templateBuffer = await templateRes.arrayBuffer()
        // 오프라인 대비 사본 — 예전엔 같은 zip을 사본용으로 한 번 더 받았다. 저장은 기다리지 않고,
        // 크기가 같은 사본이 이미 있으면 다시 쓰지 않는다(매 진입 zip 10개 base64 쓰기가 편집기 첫 마운트와 겹쳤다)
        if (isApp() && template.url?.startsWith('storage://')) {
          const url = template.url
          const buffer = templateBuffer
          AppFileManager.localCopySize(url)
            .then(localSize => {
              if (localSize === buffer.byteLength) return
              const copy = new File([buffer], AppFileManager.getFileName(url))
              return AppFileManager.saveResourceBlob(url, copy)
            })
            .catch(error => console.warn('[docs] 템플릿 로컬 사본 저장 실패:', error))
        }
      } else {
        // 인터넷 미연결: 로컬 파일 사용
        const localPath = await AppFileManager.toCapacitorPath(
          template.url as `storage://${string}`,
        )
        const localRes = await fetch(localPath)
        if (!localRes.ok) throw new Error(`Failed to load local template: ${localRes.status}`)
        templateBuffer = await localRes.arrayBuffer()
      }
    } else {
      throw new Error(
        `Template file not available: ${template.url} (network disconnected and no local file)`,
      )
    }
    // zip 파일 압축 해제 및 scriptModule 추출
    const scriptModule = await new Promise<ScriptModule>((resolve, reject) => {
      fflate.unzip(new Uint8Array(templateBuffer), (err, files) => {
        if (err) {
          reject(err)
          return
        }
        const scriptFile = files[SCRIPT_FILE_NAME]
        if (!scriptFile) {
          reject(new Error(`${SCRIPT_FILE_NAME} not found in zip file`))
          return
        }
        const scriptCode = new TextDecoder().decode(scriptFile)
        const module = new Function(
          `
            ${scriptCode.replace(/export\s+/g, '').replace(/default\s+/g, '')}
            return { mergeOrGenerateData };
          `,
        )() as ScriptModule
        resolve(module)
      })
    })

    return { template, scriptModule }
  }

  /**
   * 준비된 템플릿 모듈을 사용하여 특정 시설물의 문서 생성
   * @param taskData - 과업 데이터
   * @param facility - 시설물 데이터
   * @param originalDocRecords - 기존 문서 레코드
   * @param templateModules - 준비된 템플릿 모듈 배열
   * @returns docItems와 facilityRecords
   */
  async function generateDocsForFacility(
    taskData: Task,
    facility: Facility,
    originalDocRecords: UserExportReportRecord[],
    templateModules: Array<{ template: ExportTemplate; scriptModule: ScriptModule }>,
  ): Promise<{ docItems: DocItem[]; facilityRecords: UserExportReportRecord[] }> {
    const facilityRecords: UserExportReportRecord[] = []

    const docItems = await Promise.all(
      templateModules.map(async ({ template, scriptModule }) => {
        const docData = await scriptModule.mergeOrGenerateData(
          taskData,
          facility,
          originalDocRecords,
          template.id,
          mergeRecordToDefaultGenData,
        )
        docData.template_key = template.template_key as string
        docData.template_type = template.type as string
        facilityRecords.push(...docData.pages)
        return docData
      }),
    )

    return { docItems, facilityRecords }
  }

  const currentReport = ref<UserExportReport | null>(null)
  const currentReportData = ref<TreeViewData[]>([])
  const templates = ref<ExportTemplate[]>([])
  const cachedTemplateModules = ref<
    Array<{ template: ExportTemplate; scriptModule: ScriptModule }>
  >([])
  async function initAllData(taskData: Task, gen: number): Promise<void> {
    if (!taskData) return
    const facilityList = taskData.facilities
    const company = currentCompanyCode.value
    if (!company) return

    // 원본 데이터 백업 (병합된 docRecords)
    const originalDocRecords = cloneDeep(docRecords.value.filter(item => !item.deleted_at))
    const returnVal: TreeViewData[] = []
    const newDocRecords: UserExportReportRecord[] = []

    // 1단계: 템플릿 리스트 가져오기 (한 번만 실행)
    let reports: UserExportReport[] = []
    if (isApp()) {
      if ((await Network.getStatus()).connected) {
        templates.value = await APIServer.getUserExportTemplateList()
        localStorage.setItem('templates', JSON.stringify(templates.value))

        // 보고서 리스트 가져오기
        reports = await APIServer.getUserExportReportList()
        localStorage.setItem('reports', JSON.stringify(reports))
      } else {
        templates.value = JSON.parse(localStorage.getItem('templates') || '[]')
        reports = JSON.parse(localStorage.getItem('reports') || '[]')
      }
    } else {
      templates.value = await APIServer.getUserExportTemplateList()
      reports = await APIServer.getUserExportReportList()
    }
    //TODO: 보고서의 첫번쨰를 선택하고있지만, 원하는 보고서를 선택하는 ui가 들어가야함
    currentReport.value = reports && reports.length > 0 ? reports[0] : null
    let templateList: ExportTemplate[] = []
    cachedTemplateModules.value = []
    if (currentReport.value) {
      localStorage.setItem(`${taskData.id}_currentReport`, JSON.stringify(currentReport.value))
      templateList = templates.value.filter(template =>
        currentReport.value?.template_ids.includes(template.id),
      )
    } else {
      const localCurrentReport = localStorage.getItem(`${taskData.id}_currentReport`) ?? null
      if (localCurrentReport) {
        currentReport.value = JSON.parse(localCurrentReport)
        templateList = templates.value.filter(
          template => currentReport.value?.template_ids.includes(template.id) ?? false,
        )
      }
    }
    if (!templateList || templateList.length === 0) {
      return
    }
    templateList = templateList.sort((a, b) => Number(a.id) - Number(b.id))
    if (!currentReport.value) return
    // 2단계: 모든 템플릿의 zip 파일 다운로드 및 scriptModule 추출 (한 번만 실행)
    const networkStatus = await Network.getStatus()
    const fflate = await loadFflate()

    const templateModules = await Promise.all(
      templateList.map(template => loadTemplateScriptModule(template, networkStatus, fflate)),
    )
    // 템플릿 모듈 캐싱 (addNewFacilityDocs에서 재사용) — 추월된 회차가 새 회차의 캐시를 덮지 않게
    if (gen !== initGeneration) return
    cachedTemplateModules.value = templateModules

    // 3단계: 각 시설물에 대해 준비된 템플릿 모듈 사용
    for (const facility of facilityList) {
      let typeList = TYPE_LIST
      const { docItems, facilityRecords } = await generateDocsForFacility(
        taskData,
        facility,
        originalDocRecords,
        templateModules,
      )

      // typeList 중 docItems에 있는 template_type과 일치하는 것만 남기기
      typeList = typeList.filter(type => docItems.some(item => item.template_type === type))
      returnVal.push({
        id: facility.id.toString(),
        label: facility.name,
        checked: false,
        // @ts-expect-error - TreeViewData의 children 타입이 재귀적이어서 DocItem과 호환되지 않음
        children: typeList.map(type => ({
          id: type,
          label: type,
          checked: false,
          children: docItems
            .filter(item => item.template_type === type)
            .map(item => ({
              ...item,
              id: `${currentTaskDatabaseId.value}_${facility.id}_${item.docTitle}`,
              label: item.docTitle,
              checked: false,
            })),
        })),
      })
      newDocRecords.push(...facilityRecords)
    }

    // 모든 처리가 완료된 후 업데이트
    if (gen !== initGeneration) return
    docRecords.value = newDocRecords
    currentReportData.value = returnVal
    // 문서 상태는 여기서 준비 끝 — 아래 이미지 미리 받기는 오프라인 대비라 기다리게 하지 않는다
    isPreparing.value = false

    // 보고서 이미지 미리 다운로드(앱용)
    await downloadReportImages()

    // if (!selectedFacilityId.value) {
    //   console.info('currentReportData', currentReportData.value)
    // }
  }

  async function downloadReportImages(): Promise<void> {
    if (!isApp()) return
    // 있는 파일은 readdir 1회로 거른다(건별 stat은 A21s 63건 직렬에 1~2s). 목록을 못 읽으면 건별 확인
    const localNames = await AppFileManager.listLocalFileNames()
    for (const record of docRecords.value) {
      if (!record.url) continue
      if (localNames?.has(AppFileManager.getFileName(record.url))) continue
      await AppFileManager.checkFileExistsAndDownloaded(record.url)
    }
  }

  const requestUpdate = async (datas: UserExportReportRecord[]): Promise<void> => {
    const { itemsToProcess, columns } = await getItemToProcess(datas, storeKey)
    await updateSqliteOnly(itemsToProcess, columns)
    await addApiQueue(itemsToProcess)
  }

  const updateSqliteOnly = async (
    itemsToProcess: { item: UserExportReportRecord; itemId: string }[],
    columns: string[],
  ): Promise<void> => {
    try {
      if (itemsToProcess.length === 0) return

      const db = await SQL.getDatabase()
      // 4. 배치 처리를 위한 설정
      const insertColumns = ['id', 'task_id']
      const BATCH_SIZE = 50 // SQLite 변수 제한을 고려한 배치 크기

      // 유효한 컬럼 찾기
      for (const [key] of Object.entries(itemsToProcess[0].item)) {
        if (key === 'id') continue
        if (columns.includes(key)) {
          insertColumns.push(key)
        }
      }

      // 5. 배치 단위로 데이터 처리
      for (let i = 0; i < itemsToProcess.length; i += BATCH_SIZE) {
        const batch = itemsToProcess.slice(i, i + BATCH_SIZE)
        const batchValuePlaceholders = []
        const batchValues = []

        // 각 배치에 대한 값과 플레이스홀더 준비
        for (const { item, itemId } of batch) {
          const valuePlaceholders = ['?', '?'] // id, task_id 플레이스홀더
          batchValues.push(itemId, currentTaskDatabaseId.value)

          for (let j = 2; j < insertColumns.length; j++) {
            const key = insertColumns[j]
            valuePlaceholders.push('?')
            batchValues.push(prepareSQLiteValue((item as unknown as Record<string, unknown>)[key]))
          }
          batchValuePlaceholders.push(`(${valuePlaceholders.join(', ')})`)
        }

        // 배치 단위로 UPSERT 쿼리 실행
        const sql = `
                        INSERT OR REPLACE INTO ${storeKey}
                        (${insertColumns.join(', ')})
                        VALUES ${batchValuePlaceholders.join(', ')}
                    `
        await db.run(sql, batchValues)
      }
    } catch (e) {
      console.error('Error in updateOnlySqlite:', e)
      throw e
    }
  }

  const addApiQueue = async (
    itemsToProcess: { item: UserExportReportRecord; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return

    for (const { item } of itemsToProcess) {
      // 로컬에서 변경된 항목만 동기화 대상으로 삼습니다.
      if (!item.modifiedFromLocal) continue
      addDocJobToQueue(item)
    }
  }

  function needUploadFile(item: UserExportReportRecord): boolean {
    const items = typeof item.items === 'string' ? JSON.parse(item.items) : item.items
    if (!items) return false

    // file 타입 체크
    const file = items.find(
      (item: DocEditPageObj) =>
        item.type === 'file' &&
        (item.value?.startsWith('local-file://') ||
          item.value?.startsWith('data:image') ||
          item.value?.startsWith('android-local-file:/') ||
          item.value?.startsWith('android-file:/')),
    )
    if (file) return true

    // multi_file 타입 체크
    const multiFile = items.find((item: DocEditPageObj) => {
      if (item.type !== 'multi_file' || !item.items || !Array.isArray(item.items)) return false
      // multi_file의 items 배열 내부 확인
      return item.items.some((subItem: unknown) => {
        const docSubItem = subItem as DocEditPageObj
        const value = docSubItem.value
        return (
          value &&
          typeof value === 'string' &&
          (value.startsWith('local-file://') ||
            value.startsWith('data:image') ||
            value.startsWith('android-local-file:/') ||
            value.startsWith('android-file://'))
        )
      })
    })

    return !!multiFile
  }

  /**
   * UserExportReportRecord 동기화 작업을 SyncQueue에 추가하는 헬퍼 함수
   * @param item 동기화할 UserExportReportRecord 데이터
   */
  function addDocJobToQueue(item: UserExportReportRecord): void {
    if (!item.modifiedFromLocal) return

    const isNew = String(item.id).includes('_need_to_upload')
    const needsFileUpload = needUploadFile(item)

    let executeCallback: (data: UserExportReportRecord) => Promise<UserExportReportRecord>
    let jobPriority = JobPriority.NORMAL

    // 중복 작업 방지를 위한 검사
    const existingJobs = syncQueue
      .getQueueState()
      .filter(
        job =>
          job.localStringId === item.id.toString() &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING),
      )

    if (existingJobs.length > 0) {
      console.warn(
        `[Docs] Job for ${item.id.toString()} already exists in queue. Skipping duplicate.`,
      )
      return
    }

    if (isNew) {
      // --- 생성 작업: 파일 먼저 업로드 → 레코드 생성 ---
      jobPriority = JobPriority.LOW // 파일 업로드는 우선순위 낮춤
      executeCallback = async (data: UserExportReportRecord): Promise<UserExportReportRecord> => {
        const parsedItems = typeof data.items === 'string' ? JSON.parse(data.items) : data.items

        // 파일 업로드가 필요한 경우
        if (parsedItems && needsFileUpload) {
          for (const docItem of parsedItems) {
            // file 타입 처리
            if (docItem.type === 'file') {
              if (!docItem.value || docItem.value === '' || docItem.value === null) continue

              const localFilePath = docItem.value as LocalFileResourcePath

              // storage://로 시작하면 이미 업로드 완료 (SQLite만 업데이트된 상태)
              if (docItem.value.startsWith('storage://')) {
                continue // 이미 업로드됨
              }

              // 파일 존재 확인
              if (!(await AppFileManager.checkFileExists(localFilePath))) {
                console.warn(
                  `[Docs] File not found: ${localFilePath}. Clearing value instead of retrying.`,
                )
                docItem.value = '' // 파일이 없으면 빈 값으로 설정
                continue
              }

              // WebP 변환
              const converted = await convertFileToWebp({ resourcePath: localFilePath })
              if (!converted.value) {
                throw new Error('File conversion to WebP failed.')
              }

              const webpBase64 = converted.value
              const splitPath = localFilePath.split('_')
              const taskDatabaseId = splitPath[4] ?? currentTaskDatabaseId.value ?? 'local'
              const facilityBranchDatabaseId = splitPath[5] ?? 'local'
              const templateId = splitPath[6] ?? 'local'
              const fileName = `${data.localStringId}_${docItem.id}_${getUnixtime()}.webp`
              const file = await base64ToFile(webpBase64, fileName)

              if (!file) {
                throw new Error('File conversion failed for new doc item.')
              }

              // 1단계: 파일을 버킷에 먼저 업로드
              const resourceUrl = await CloudFile.uploadFile(
                'homecheck-service',
                `${GCS_ROOT_PATH}/task/${taskDatabaseId}/facility_branch/${facilityBranchDatabaseId}/template/${templateId}/${fileName}`,
                file,
              )

              if (!resourceUrl) throw new Error('Cloud upload failed for new doc item.')

              // storage://로 시작하는지 확인
              if (!resourceUrl.startsWith('storage://')) {
                throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
              }

              // 메모리만 업데이트 (SQLite는 onSuccess에서 일괄 업데이트)
              docItem.value = resourceUrl
            }
            // multi_file 타입 처리
            else if (docItem.type === 'multi_file') {
              if (!docItem.items || !Array.isArray(docItem.items)) continue

              for (let i = 0; i < docItem.items.length; i++) {
                const subItem = docItem.items[i] as DocEditPageObj
                if (!subItem.value || subItem.value === '' || subItem.value === null) continue
                if (typeof subItem.value !== 'string') continue

                const localFilePath = subItem.value as LocalFileResourcePath

                // storage://로 시작하면 이미 업로드 완료
                if (subItem.value.startsWith('storage://')) {
                  continue
                }

                // 파일 존재 확인
                if (!(await AppFileManager.checkFileExists(localFilePath))) {
                  console.warn(
                    `[Docs] Multi-file not found: ${localFilePath}. Clearing value instead of retrying.`,
                  )
                  subItem.value = ''
                  continue
                }

                // WebP 변환
                const converted = await convertFileToWebp({ resourcePath: localFilePath })
                if (!converted.value) {
                  throw new Error('Multi-file conversion to WebP failed.')
                }

                const webpBase64 = converted.value
                const splitPath = localFilePath.split('_')
                const taskDatabaseId = splitPath[4] ?? currentTaskDatabaseId.value ?? 'local'
                const facilityBranchDatabaseId = splitPath[5] ?? 'local'
                const templateId = splitPath[6] ?? 'local'
                const fileName = `${data.localStringId}_${docItem.id}_${i}_${getUnixtime()}.webp`
                const file = await base64ToFile(webpBase64, fileName)

                if (!file) {
                  throw new Error('Multi-file conversion failed for new doc item.')
                }

                // 파일을 버킷에 업로드
                const resourceUrl = await CloudFile.uploadFile(
                  'homecheck-service',
                  `${GCS_ROOT_PATH}/task/${taskDatabaseId}/facility_branch/${facilityBranchDatabaseId}/template/${templateId}/${fileName}`,
                  file,
                )

                if (!resourceUrl) throw new Error('Cloud upload failed for new multi-file item.')

                // storage://로 시작하는지 확인
                if (!resourceUrl.startsWith('storage://')) {
                  throw new Error(`Multi-file upload failed: invalid URL format (${resourceUrl})`)
                }

                // 메모리만 업데이트
                subItem.value = resourceUrl
              }
            }
          }
        }

        // 2단계: 레코드 생성
        const [newRecord] = await APIServer.addUserExportReportRecord([
          {
            facility_id: data.facility_id as number,
            template_id: data.template_id,
            task_id: data.task_id as number,
            page_no: data.page_no,
            items: parsedItems,
            brush_data: data.brush_data ?? [],
          },
        ])

        if (!newRecord || !newRecord.id) {
          throw new Error('Failed to create record on server.')
        }

        return {
          ...data,
          id: newRecord.id,
          items: parsedItems,
          brush_data: data.brush_data ?? [],
        }
      }
    } else {
      // --- 업데이트 작업 ---
      if (needsFileUpload) {
        jobPriority = JobPriority.LOW // 파일 업로드는 우선순위 낮춤
        executeCallback = async (data: UserExportReportRecord): Promise<UserExportReportRecord> => {
          const parsedItems = typeof data.items === 'string' ? JSON.parse(data.items) : data.items

          if (parsedItems) {
            for (const docItem of parsedItems) {
              // file 타입 처리
              if (docItem.type === 'file') {
                if (!docItem.value || docItem.value === '' || docItem.value === null) continue

                const localFilePath = docItem.value as LocalFileResourcePath

                // storage://로 시작하면 이미 업로드 완료
                if (docItem.value.startsWith('storage://')) {
                  continue
                }

                // 파일 존재 확인
                if (!(await AppFileManager.checkFileExists(localFilePath))) {
                  console.warn(
                    `[Docs] File not found: ${localFilePath}. Clearing value instead of retrying.`,
                  )
                  docItem.value = ''
                  continue
                }

                // WebP 변환
                const converted = await convertFileToWebp({ resourcePath: localFilePath })
                if (!converted.value) {
                  throw new Error('File conversion to WebP failed.')
                }

                const webpBase64 = converted.value
                const splitPath = localFilePath.split('_')
                const taskDatabaseId = splitPath[4] ?? currentTaskDatabaseId.value ?? 'local'
                const facilityBranchDatabaseId = splitPath[5] ?? 'local'
                const templateId = splitPath[6] ?? 'local'
                const fileName = `${data.id}_${docItem.id}_${getUnixtime()}.webp`
                const file = await base64ToFile(webpBase64, fileName)

                if (!file) {
                  throw new Error('File conversion failed for update doc item.')
                }

                // 파일 업로드
                const resourceUrl = await CloudFile.uploadFile(
                  'homecheck-service',
                  `${GCS_ROOT_PATH}/task/${taskDatabaseId}/facility_branch/${facilityBranchDatabaseId}/template/${templateId}/${fileName}`,
                  file,
                )

                if (!resourceUrl) throw new Error('Cloud upload failed for update doc item.')

                // storage://로 시작하는지 확인
                if (!resourceUrl.startsWith('storage://')) {
                  throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
                }

                // GCS 업로드 성공 시 SQLite에 즉시 업데이트
                docItem.value = resourceUrl
                await individualSetLocalModified(data.id.toString(), [
                  { key: 'items', value: JSON.stringify(parsedItems) },
                ])
              }
              // multi_file 타입 처리
              else if (docItem.type === 'multi_file') {
                if (!docItem.items || !Array.isArray(docItem.items)) continue

                for (let i = 0; i < docItem.items.length; i++) {
                  const subItem = docItem.items[i] as DocEditPageObj
                  if (!subItem.value || subItem.value === '' || subItem.value === null) continue
                  if (typeof subItem.value !== 'string') continue

                  const localFilePath = subItem.value as LocalFileResourcePath

                  // storage://로 시작하면 이미 업로드 완료
                  if (subItem.value.startsWith('storage://')) {
                    continue
                  }

                  // 파일 존재 확인
                  if (!(await AppFileManager.checkFileExists(localFilePath))) {
                    console.warn(
                      `[Docs] Multi-file not found: ${localFilePath}. Clearing value instead of retrying.`,
                    )
                    subItem.value = ''
                    continue
                  }

                  // WebP 변환
                  const converted = await convertFileToWebp({ resourcePath: localFilePath })
                  if (!converted.value) {
                    throw new Error('Multi-file conversion to WebP failed.')
                  }

                  const webpBase64 = converted.value
                  const splitPath = localFilePath.split('_')
                  const taskDatabaseId = splitPath[4] ?? currentTaskDatabaseId.value ?? 'local'
                  const facilityBranchDatabaseId = splitPath[5] ?? 'local'
                  const templateId = splitPath[6] ?? 'local'
                  const fileName = `${data.id}_${docItem.id}_${i}_${getUnixtime()}.webp`
                  const file = await base64ToFile(webpBase64, fileName)

                  if (!file) {
                    throw new Error('Multi-file conversion failed for update doc item.')
                  }

                  // 파일 업로드
                  const resourceUrl = await CloudFile.uploadFile(
                    'homecheck-service',
                    `${GCS_ROOT_PATH}/task/${taskDatabaseId}/facility_branch/${facilityBranchDatabaseId}/template/${templateId}/${fileName}`,
                    file,
                  )

                  if (!resourceUrl)
                    throw new Error('Cloud upload failed for update multi-file item.')

                  // storage://로 시작하는지 확인
                  if (!resourceUrl.startsWith('storage://')) {
                    throw new Error(`Multi-file upload failed: invalid URL format (${resourceUrl})`)
                  }

                  // GCS 업로드 성공 시 SQLite에 즉시 업데이트
                  subItem.value = resourceUrl
                  await individualSetLocalModified(data.id.toString(), [
                    { key: 'items', value: JSON.stringify(parsedItems) },
                  ])
                }
              }
            }
          }

          // 레코드 업데이트
          const updateData = {
            id: Number(data.id),
            items: parsedItems,
            brush_data: data.brush_data ?? [],
          }
          await APIServer.updateUserExportReportRecord([updateData])

          return {
            ...data,
            items: parsedItems,
            brush_data: data.brush_data ?? [],
          }
        }
      } else {
        // --- 단순 메타데이터 업데이트 (파일 업로드 불필요) ---
        executeCallback = async (data: UserExportReportRecord): Promise<UserExportReportRecord> => {
          const updateData = {
            id: Number(data.id),
            items: data.items,
            brush_data: data.brush_data,
          }
          await APIServer.updateUserExportReportRecord([updateData])

          return data
        }
      }
    }

    syncQueue.addJob({
      localStringId: item.localStringId || item.id.toString(),
      data: item,
      priority: jobPriority,
      maxRetries: 5,
      tag: 'docs',
      execute: executeCallback,

      onSuccess: async (result, data) => {
        // targetId 안전하게 추출
        const targetId = data.localStringId || (data.id ? data.id.toString() : null)
        if (!targetId) {
          console.error('[Docs] onSuccess: targetId를 확인할 수 없습니다', data)
          return
        }

        // 업로드 완료 여부 확인
        let hasUnuploadedFiles = false
        if (result?.items) {
          const items = typeof result.items === 'string' ? JSON.parse(result.items) : result.items
          hasUnuploadedFiles = items.some((item: DocEditPageObj) => {
            // file 타입 체크
            if (
              item.type === 'file' &&
              item.value &&
              typeof item.value === 'string' &&
              (item.value.startsWith('local-file://') ||
                item.value.startsWith('android-local-file://') ||
                item.value.startsWith('android-file://'))
            ) {
              return true
            }
            // multi_file 타입 체크
            if (item.type === 'multi_file' && item.items && Array.isArray(item.items)) {
              return item.items.some((subItem: unknown) => {
                const docSubItem = subItem as DocEditPageObj
                return (
                  docSubItem.value &&
                  typeof docSubItem.value === 'string' &&
                  (docSubItem.value.startsWith('local-file://') ||
                    docSubItem.value.startsWith('android-local-file://') ||
                    docSubItem.value.startsWith('android-file://'))
                )
              })
            }
            return false
          })
        }

        const updateValues: { key: string; value: unknown }[] = [
          { key: 'updated_at', value: Merge.getCurrentTime() },
        ]

        // 업로드되지 않은 파일이 없을 때만 modifiedFromLocal을 false로 설정
        if (!hasUnuploadedFiles) {
          updateValues.push({ key: 'modifiedFromLocal', value: false })
        }

        if (result?.items) {
          updateValues.push({ key: 'items', value: JSON.stringify(result.items) })
        }

        if (result?.brush_data) {
          updateValues.push({ key: 'brush_data', value: JSON.stringify(result.brush_data) })
        }

        await individualSetLocalModified(targetId, updateValues)
      },

      onError: (error, data) => {
        console.error(`[SyncQueue] Docs job for ${data.id} FAILED.`, error)
      },

      onIdMap: isNew
        ? async (localStringId: string, serverResult: UserExportReportRecord): Promise<void> => {
            // 중복 항목 확인 및 제거
            const db = await SQL.getDatabase()
            const existingItems = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ?`,
              [localStringId],
            )

            if (existingItems.values && existingItems.values.length > 1) {
              console.warn(
                `[SyncQueue] Found ${existingItems.values.length} duplicate items for ${localStringId}. Cleaning up...`,
              )
              // _need_to_upload가 포함된 항목 삭제
              await db.execute(
                `DELETE FROM ${storeKey} WHERE localStringId = ? AND id LIKE '%_need_to_upload'`,
                [localStringId],
              )
            }

            // 메모리 상의 데이터 업데이트
            const recordInStore = docRecords.value.find(r => r?.localStringId === localStringId)
            if (recordInStore) {
              recordInStore.id = serverResult.id
              recordInStore.items = serverResult.items
              recordInStore.brush_data = serverResult.brush_data
              recordInStore.modifiedFromLocal = false
            }

            // 데이터베이스 업데이트
            await individualSetLocalModified(localStringId, [
              { key: 'id', value: serverResult.id },
              { key: 'items', value: JSON.stringify(serverResult.items) },
              { key: 'brush_data', value: JSON.stringify(serverResult.brush_data) },
              { key: 'modifiedFromLocal', value: false },
            ])
          }
        : undefined,
    })
  }

  async function uploadFile(
    pageItem: UserExportReportRecord,
    item: DocEditPageObj,
    selectedIds: { taskId: number; facilityId: number; templateId: number },
    event: Event,
  ): Promise<string | undefined> {
    const stateStore = useAppStateStore()
    try {
      stateStore.setIsLoading(true)
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return

      // 이미지 파일인지 확인
      if (!file.type.startsWith('image/')) {
        App.toastInstance.add('error', '이미지 파일만 업로드 가능합니다.')
        return
      }

      // webp로 변환
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      const imageUrl = URL.createObjectURL(file)
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = imageUrl
      })

      canvas.width = img.width
      canvas.height = img.height
      ctx?.drawImage(img, 0, 0)

      const webpBlob = await new Promise<Blob | null>(resolve => {
        canvas.toBlob(resolve, 'image/webp')
      })

      if (!webpBlob) throw new Error('Failed to convert to webp')

      // const fileName = getRandomString(10, false)
      const newFile = new File([webpBlob], `${pageItem.page_no}_${item.id}_${getUnixtime()}.webp`, {
        type: 'image/webp',
      })

      const resourceUrl = await CloudFile.uploadFile(
        'homecheck-service',
        `${GCS_ROOT_PATH}/task/${selectedIds.taskId}/facility_branch/${selectedIds.facilityId}/template/${selectedIds.templateId}/${newFile.name}`,
        newFile,
      )

      if (!resourceUrl) throw new Error('Failed to upload file')

      return resourceUrl
    } catch (error) {
      console.error('Error in uploadFile:', error)
    } finally {
      stateStore.setIsLoading(false)
    }
  }

  //시설물 사진 다중선택에 필요한 요소
  const currentFacilityPhotoItems = ref<DocEditPageObj[]>([])
  //선택 시점에 현재 문서 전체페이지에서 사진이 없는(url이 없는 항목들을 필터링
  function setCurrentFacilityPhotoItems(): void {
    const findFacilityTemplates = currentReportData.value.find(
      (item: TreeViewData) => item.id == selectedFacilityId.value?.toString(),
    )
    if (!findFacilityTemplates) return
    const mergeAllTypeTemplates = findFacilityTemplates?.children?.flatMap(
      (item: any) => item.children,
    )
    const findTemplate = mergeAllTypeTemplates?.find(
      (item: any) => item.template_id == selectedTemplateId.value,
    )
    if (!findTemplate || !findTemplate.pages) return

    const currentAllFileItems = findTemplate.pages.flatMap((doc: any) => {
      if (doc.items) {
        return doc.items
          .filter((item: DocEditPageObj) => item.type === 'multi_file' && !item.value)
          .map((item: UserExportReportRecord) => ({
            ...item,
            page_no: doc.page_no,
          }))
      }
      return []
    })
    currentFacilityPhotoItems.value = currentAllFileItems
  }

  const settedShapeTableData = ref<ShapeForPhotos[]>([])
  const updateShapeTableData = (data: ShapeForPhotos): void => {
    if (settedShapeTableData.value.find(x => x.localStringId == data.localStringId)) {
      settedShapeTableData.value = settedShapeTableData.value.map(x => {
        if (x.localStringId == data.localStringId) {
          return data
        }
        return x
      })
    }
    settedShapeTableData.value.push(data)
  }

  function clearAllState(): void {
    selectedFacilityId.value = null
    selectedTemplateId.value = null
    selectedTemplateKey.value = null
    selectedNode.value = null
    currentFacilityPhotoItems.value = []
    settedShapeTableData.value = []
  }

  function getCurrentTemplate(templateId: number): ExportTemplate | null {
    return templates.value.find(template => template.id == templateId) || null
  }

  /**
   * 새로운 시설물에 대한 문서를 생성하고 currentReportData와 docRecords에 추가
   * @param facilityId - 추가할 시설물 ID
   */
  async function addNewFacilityDocs(facilityId: number): Promise<void> {
    // 준비 중이면 끝난 뒤에(준비가 docRecords·currentReportData를 통째로 바꾸므로)
    await initPromise
    const allTaskStore = useAllTaskStore()
    const currentTaskData = allTaskStore.currentTaskData
    if (!currentTaskData) return

    const findNewFacility = currentTaskData.facilities.find(facility => facility.id == facilityId)
    if (!findNewFacility) return

    // 캐싱된 템플릿 모듈이 없으면 리턴
    if (cachedTemplateModules.value.length === 0) return

    // 기존 문서 레코드에서 삭제되지 않은 것만 사용
    const originalDocRecords = cloneDeep(docRecords.value.filter(item => !item.deleted_at))

    // 새 시설물에 대한 문서 생성
    const { docItems, facilityRecords } = await generateDocsForFacility(
      currentTaskData,
      findNewFacility,
      originalDocRecords,
      cachedTemplateModules.value,
    )

    // typeList 필터링
    let typeList = TYPE_LIST
    typeList = typeList.filter(type => docItems.some(item => item.template_type === type))

    // TreeViewData 생성
    const newFacilityTreeData: TreeViewData = {
      id: findNewFacility.id.toString(),
      label: findNewFacility.name,
      checked: false,
      // @ts-expect-error - TreeViewData의 children 타입이 재귀적이어서 DocItem과 호환되지 않음
      children: typeList.map(type => ({
        id: type,
        label: type,
        checked: false,
        children: docItems
          .filter(item => item.template_type === type)
          .map(item => ({
            ...item,
            id: `${currentTaskDatabaseId.value}_${findNewFacility.id}_${item.docTitle}`,
            label: item.docTitle,
            checked: false,
          })),
      })),
    }
    // currentReportData에 추가
    currentReportData.value = [...currentReportData.value, newFacilityTreeData]

    // docRecords에 추가
    docRecords.value = [...docRecords.value, ...facilityRecords]
  }

  /**
   * 시설물에 해당하는 문서를 currentReportData와 docRecords에서 제거
   * @param facilityId - 삭제할 시설물 ID
   */
  async function deleteFacilityDocs(facilityId: number): Promise<void> {
    await initPromise
    // currentReportData에서 해당 시설물 제거
    currentReportData.value = currentReportData.value.filter(
      item => item.id !== facilityId.toString(),
    )

    if (isApp()) {
      // 앱 환경: 해당 시설물의 레코드를 soft delete 처리
      const targetRecords = docRecords.value.filter(record => record.facility_id == facilityId)

      // 삭제 상태를 먼저 설정한 후 SQLite에 저장 (순서 중요)
      for (const record of targetRecords) {
        record.deleted_at = Merge.getCurrentTime()
        record.modifiedFromLocal = false

        // syncQueue에서 해당 작업 제거
        if (record.localStringId) {
          syncQueue.removeJobByLocalStringId(record.localStringId)
        }
      }
      const { itemsToProcess, columns } = await getItemToProcess(targetRecords, storeKey)
      await updateSqliteOnly(itemsToProcess, columns)

      docRecords.value = docRecords.value.filter(record => record.facility_id !== facilityId)
    } else {
      // 웹 환경: 단순히 필터링하여 제거
      docRecords.value = docRecords.value.filter(record => record.facility_id !== facilityId)
    }
  }

  //시설물 이름 바뀌면 여기도 업데이트
  async function updateFacilityNameDocs(facilityId: number): Promise<void> {
    const allTaskStore = useAllTaskStore()
    const currentTaskData = allTaskStore.currentTaskData
    if (!currentTaskData) return

    const findNewFacility = currentTaskData.facilities.find(facility => facility.id == facilityId)
    if (!findNewFacility) return

    const findFacilityDocIndex = currentReportData.value.findIndex(
      item => item.id == facilityId.toString(),
    )
    if (findFacilityDocIndex !== -1) {
      currentReportData.value[findFacilityDocIndex].label = findNewFacility.name
    }
  }

  /**
   * 현재 선택된 문서 페이지의 최신 데이터를 서버에서 가져와 갱신
   * docRecords, currentReportData, selectedNode를 모두 업데이트
   * @param facility_id - 시설물 ID
   * @param template_id - 템플릿 ID
   */
  async function refreshCurrentDocPage(facility_id: number, template_id: number): Promise<void> {
    const allTaskStore = useAllTaskStore()
    const currentTaskData = allTaskStore.currentTaskData
    const task_id: number = Number(currentTaskData?.id ?? 0)
    if (!task_id || !facility_id || !template_id) return

    // 네트워크 확인 (앱 환경)
    if (isApp() && !(await checkAppNetwork())) {
      return
    }

    try {
      // 서버에서 최신 데이터 가져오기
      const serverRecords = await getCurrentServerData(
        task_id,
        Number(facility_id),
        Number(template_id),
      )

      if (!serverRecords || serverRecords.length === 0) {
        console.info('[refreshCurrentDocPage] 서버에서 가져온 데이터가 없습니다.')
        return
      }

      // 1. docRecords 업데이트 - 서버 데이터로 기존 레코드 교체 또는 추가
      for (const serverRecord of serverRecords) {
        const existingIndex = docRecords.value.findIndex(
          (r: UserExportReportRecord) =>
            r.task_id == serverRecord.task_id &&
            r.facility_id == serverRecord.facility_id &&
            r.template_id == serverRecord.template_id &&
            r.page_no == serverRecord.page_no,
        )

        // 로컬에서 수정 중인 데이터는 덮어쓰지 않음
        if (existingIndex !== -1) {
          const existingRecord = docRecords.value[existingIndex]
          if (existingRecord.modifiedFromLocal) {
            console.info(
              `[refreshCurrentDocPage] 로컬 수정 중인 레코드 스킵: page_no=${serverRecord.page_no}`,
            )
            continue
          }
          // 기존 레코드 업데이트
          const targetUpdate = {
            ...docRecords.value[existingIndex],
            items: serverRecord.items,
            brush_data: serverRecord.brush_data,
          }
          docRecords.value[existingIndex] = targetUpdate

          if (isApp()) {
            const { itemsToProcess, columns } = await getItemToProcess([targetUpdate], storeKey)
            await updateSqliteOnly(itemsToProcess, columns)
          }
        } else {
          console.warn('구조상 없을리가 없지만 없으면 문제임')
        }
      }
      // 2. currentReportData와 selectedNode 업데이트
      await reloadCurrentReportDataOnRecords()
    } catch (error) {
      console.error('[refreshCurrentDocPage] 오류:', error)
    }
  }

  return {
    // 데이터
    docRecords,
    // 보고서 데이터
    currentReportData,
    templates,

    // 선택 상태
    selectedFacilityId,
    selectedTemplateKey,
    selectedTemplateId,
    selectedNode,

    currentFacilityPhotoItems,
    setCurrentFacilityPhotoItems,

    refreshCurrentDocPage,

    // 함수들
    getLocalData,
    getServerData,
    initData,
    whenReady,
    isPreparing,
    updatePageItemValue,
    getAllModifiedData,
    uploadFile,
    reloadCurrentReportDataOnRecords,
    addNewFacilityDocs,
    deleteFacilityDocs,
    updateFacilityNameDocs,
    updateShapeTableData,
    settedShapeTableData,
    currentReport,
    clearAllState,
    updatePageBrushValue,
    deletePageBrushValue,
    syncPageBrushData,
    getCurrentTemplate,

    // 초기화
    allClear,
  }
})
